import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import moment, { Locale } from 'moment';
import 'moment-duration-format';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { Language } from '@nimbus-library/features/localization/models/enums/language.enum';
import { ILocalizationService } from '@nimbus-library/features/localization/services/i-localization.service';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';

@Pipe({
  name: 'dateFormat',
  pure: false,
  standalone: false,
})
export class DateFormatPipe implements PipeTransform, OnDestroy {
  private static baseLocale = moment.localeData('en');
  private static locales: IMap<Locale> = {};

  private value: string;

  private inputDate: moment.Moment | string | Date;
  private inputFormat: string;
  private browserLocale: boolean;

  private subscription$ = Subscription.EMPTY;

  public constructor(
    private readonly store: Store,
    private readonly localizationService: ILocalizationService,
  ) {}

  public updateValue(date: moment.Moment | string | Date, format: string, locale: string, local: boolean = false): void {
    if (this.browserLocale) {
      const key = `custom-${locale}`;
      if (!DateFormatPipe.locales[key]) {
        DateFormatPipe.locales[key] = moment.defineLocale(key, {
          parentLocale: locale,
          months: DateFormatPipe.baseLocale.months(),
          monthsShort: DateFormatPipe.baseLocale.monthsShort(),
          weekdays: DateFormatPipe.baseLocale.weekdays(),
          weekdaysShort: DateFormatPipe.baseLocale.weekdaysShort(),
        });
      }

      locale = key;
    }

    this.value = local ? moment.utc(date).locale(locale).local().format(format) : moment(date).locale(locale).format(format);
  }

  public transform(date: moment.Moment | string | Date, format: string, browserLocale: boolean, local: boolean = false): any {
    this.inputDate = date;
    this.inputFormat = format;
    this.browserLocale = browserLocale;

    const browserLanguage = this.localizationService.getBrowserLanguage(
      this.store.selectSnapshot(LocalizationState.availableLanguages),
      Language.English,
    );

    if (this.subscription$ === Subscription.EMPTY) {
      this.subscription$.add(
        combineLatest([this.store.select(LocalizationState.selectedLanguage), this.store.select(LocalizationState.selectedDateFormat)])
          .pipe(distinctUntilChanged())
          .subscribe(([, dateFormat]) => {
            const locale = browserLocale ? browserLanguage : dateFormat;

            this.updateValue(this.inputDate, this.inputFormat, locale, local);
          }),
      );
    }
    return this.value;
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
